import { useMemo } from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { useMenuNavigationProgram } from "@repo/ui";
import {
  ROUTESWEB,
  getFirstVisibleWebBottomNavTab,
  getMenuNavigationFlags,
  WEB_BOTTOM_NAV_ROUTE_PATHS,
} from "@repo/utils";
import { useIsAuthenticated } from "../../store/useAuthStore";

interface AuthGuardProps {
  requireAuth?: boolean;
}

export const AuthGuard = ({ requireAuth = true }: AuthGuardProps) => {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const { gradeId, examIds } = useMenuNavigationProgram();
  const flags = useMemo(
    () => getMenuNavigationFlags(gradeId, examIds),
    [gradeId, examIds],
  );
  const defaultSignedInPath =
    WEB_BOTTOM_NAV_ROUTE_PATHS[getFirstVisibleWebBottomNavTab(flags)];

  if (requireAuth) {
    if (!isAuthenticated) {
      const searchParams = new URLSearchParams(location.search);
      const isFromDeepLink =
        searchParams.get("isFromDeepLinking") === "true" ||
        searchParams.get("isFromSurgeVideos") === "true" ||
        searchParams.has("module");

      return (
        <Navigate
          to={`/${ROUTESWEB.AUTH.INTRO}`}
          state={isFromDeepLink ? { from: location } : {}}
          replace
        />
      );
    }
  } else {
    if (isAuthenticated) {
      const isSignupPath =
        location.pathname === `/${ROUTESWEB.AUTH.SIGNUP}` ||
        location.pathname.endsWith(`/${ROUTESWEB.AUTH.SIGNUP}`);
      const state = location.state as {
        from?: Location;
        addProfileForLoggedInUser?: boolean;
        isUpdateFlow?: boolean;
      } | null;
      if (
        isSignupPath &&
        (state?.addProfileForLoggedInUser || state?.isUpdateFlow)
      ) {
        return <Outlet />;
      }
      const fromState = (location.state as { from?: Location })?.from;
      const target = fromState
        ? `${fromState.pathname}${fromState.search || ""}`
        : defaultSignedInPath;
      return <Navigate to={target} replace />;
    }
  }

  return <Outlet />;
};
